import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Contact from '../../../models/Contact';
import { markdownToTiptapJson, tiptapToMarkdown } from '../veraPromptEditorUtils';
import { loadInitialInvestorPrompts } from './investorSandboxLoaders';
import {
  clearInvestorDraftStorage,
  refreshPreviewGoogleSearch,
  scheduleInvestorDraftSave,
  syncGroundingPrompt,
  syncIntentPrompt,
  syncPreviewPrompt,
} from './investorSandboxApi';
import { useInvestorSandboxWorkflow } from './useInvestorSandboxWorkflow';
import {
  DRAFT_KEY,
  InvestorDraft,
  contactLabel,
  draftTextEqual,
  investorDraftHasUnsavedChanges,
  parseInvestorDraft,
} from './veraGlobalChatPromptOverrideUtils';

export function useVeraGlobalChatPromptOverride() {
  const { proposalId } = useParams<{ proposalId?: string }>();
  const chatIdRef = useRef<string>(uuidv4());

  const [productTab, setProductTab] = useState(0);
  const [investorPromptTab, setInvestorPromptTab] = useState(0);
  const [rawMode, setRawMode] = useState(false);

  const [markdown, setMarkdown] = useState('');
  const productionMarkdownRef = useRef('');
  const [promptSynced, setPromptSynced] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const lastSubmittedMarkdownRef = useRef<string | null>(null);
  const savePromptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingInitRef = useRef<string | null>(null);
  const saveDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressEditorSyncRef = useRef(true);
  const userHasEditedRef = useRef(false);
  const productionBaselinesRef = useRef({ markdown: '', intent: '', grounding: '' });
  const setEditorContentRef = useRef<(value: string) => void>(() => undefined);

  const [intentText, setIntentText] = useState('');
  const intentTextRef = useRef('');
  const [intentProductionText, setIntentProductionText] = useState('');
  const [intentSynced, setIntentSynced] = useState(false);
  const [intentIsDirty, setIntentIsDirty] = useState(false);
  const intentLastSubmittedRef = useRef<string | null>(null);
  const saveIntentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [globalSearchEnabled, setGlobalSearchEnabled] = useState(false);
  const [globalSearchLinksEnabled, setGlobalSearchLinksEnabled] = useState(false);
  const [sandboxSearchEnabled, setSandboxSearchEnabled] = useState(false);
  const [sandboxSearchLocked, setSandboxSearchLocked] = useState(false);

  const [groundingText, setGroundingText] = useState('');
  const groundingTextRef = useRef('');
  const [groundingProductionText, setGroundingProductionText] = useState('');
  const [groundingSynced, setGroundingSynced] = useState(false);
  const [groundingIsDirty, setGroundingIsDirty] = useState(false);
  const groundingLastSubmittedRef = useRef<string | null>(null);
  const saveGroundingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [savedDraft, setSavedDraft] = useState<InvestorDraft | null>(null);

  const showGroundingPromptTab = globalSearchEnabled || sandboxSearchEnabled;

  const globalGuideOptions = useMemo(
    () => ({
      googleSearchEnabled: globalSearchEnabled || sandboxSearchEnabled,
      googleSearchLinksEnabled: globalSearchLinksEnabled,
    }),
    [globalSearchEnabled, sandboxSearchEnabled, globalSearchLinksEnabled],
  );

  const syncPromptNow = useCallback((value: string) => {
    if (!value.trim()) return;
    setPromptSynced(false);
    syncPreviewPrompt(chatIdRef.current, value)
      .then(() => setPromptSynced(true))
      .catch(() => setPromptSynced(false));
  }, []);

  const syncGroundingNow = useCallback((value: string) => {
    if (!value.trim()) return;
    setGroundingSynced(false);
    syncGroundingPrompt(chatIdRef.current, value)
      .then(() => setGroundingSynced(true))
      .catch(() => setGroundingSynced(false));
  }, []);

  const scheduleDraftSave = useCallback(
    (md: string, contact: Contact | null, intent?: string, grounding?: string) => {
      scheduleInvestorDraftSave(
        userHasEditedRef.current,
        {
          markdown: md,
          intentText: intent,
          groundingText: grounding ?? groundingTextRef.current,
          sandboxSearchEnabled,
          contactId: contact?.id ?? null,
          contactLabel: contactLabel(contact),
        },
        saveDraftTimerRef,
      );
    },
    [sandboxSearchEnabled],
  );

  const workflow = useInvestorSandboxWorkflow({
    chatIdRef,
    proposalId,
    setProductTab,
    prompts: {
      markdown,
      productionMarkdown: productionMarkdownRef.current,
      intentText,
      intentProductionText,
      groundingText,
      groundingProductionText,
      intentTextRef,
      groundingTextRef,
      lastSubmittedMarkdownRef,
      intentLastSubmittedRef,
      groundingLastSubmittedRef,
      userHasEditedRef,
      sandboxSearchEnabled,
      sandboxSearchLocked,
      setMarkdown,
      setEditorContent: (value) => setEditorContentRef.current(value),
      setIsDirty,
      setIntentText,
      setIntentIsDirty,
      setGroundingText,
      setGroundingIsDirty,
      setPromptSynced,
      setIntentSynced,
      setGroundingSynced,
      setSavedDraft,
      setSandboxSearchEnabled,
      syncPromptNow,
      syncGroundingNow,
      scheduleDraftSave,
    },
  });

  const editor = useEditor({
    extensions: [StarterKit],
    content: { type: 'doc', content: [{ type: 'paragraph' }] },
    onUpdate: ({ editor: ed }) => {
      if (suppressEditorSyncRef.current) return;
      userHasEditedRef.current = true;
      const md = tiptapToMarkdown(ed.getJSON());
      setMarkdown(md);
      scheduleDraftSave(
        md,
        workflow.selectedContact,
        intentTextRef.current,
        groundingTextRef.current,
      );
      if (lastSubmittedMarkdownRef.current !== null) {
        setIsDirty(md.trim() !== lastSubmittedMarkdownRef.current.trim());
      }
      setPromptSynced(false);
      if (savePromptTimerRef.current) clearTimeout(savePromptTimerRef.current);
      savePromptTimerRef.current = setTimeout(() => {
        syncPromptNow(md);
      }, 1200);
    },
  });

  const setEditorContent = useCallback(
    (value: string) => {
      if (!editor) {
        pendingInitRef.current = value;
        return;
      }
      suppressEditorSyncRef.current = true;
      editor.commands.setContent(markdownToTiptapJson(value) as any);
      requestAnimationFrame(() => {
        suppressEditorSyncRef.current = false;
      });
    },
    [editor],
  );

  setEditorContentRef.current = setEditorContent;

  useEffect(() => {
    if (!editor || pendingInitRef.current === null) return;
    setEditorContent(pendingInitRef.current);
    pendingInitRef.current = null;
  }, [editor, setEditorContent]);

  useEffect(() => {
    const draft = parseInvestorDraft(localStorage.getItem(DRAFT_KEY));
    const draftSandboxSearch = draft?.sandboxSearchEnabled;

    const reconcileDraftBanner = () => {
      if (!draft) return;
      if (!investorDraftHasUnsavedChanges(draft, productionBaselinesRef.current)) {
        clearInvestorDraftStorage();
        setSavedDraft(null);
        return;
      }
      setSavedDraft(draft);
    };

    loadInitialInvestorPrompts(draft)
      .then((loaded) => {
        productionMarkdownRef.current = loaded.productionMarkdown;
        lastSubmittedMarkdownRef.current = loaded.productionMarkdown;
        productionBaselinesRef.current.markdown = loaded.productionMarkdown;
        setIsDirty(
          draft?.markdown !== undefined &&
            !draftTextEqual(draft.markdown, loaded.productionMarkdown),
        );
        setMarkdown(loaded.markdown);
        setEditorContent(loaded.markdown);

        setIntentProductionText(loaded.intentProductionText);
        intentLastSubmittedRef.current = loaded.intentProductionText;
        productionBaselinesRef.current.intent = loaded.intentProductionText;
        intentTextRef.current = loaded.intentText;
        setIntentText(loaded.intentText);
        setIntentIsDirty(
          draft?.intentText !== undefined &&
            !draftTextEqual(draft.intentText, loaded.intentProductionText),
        );

        setGlobalSearchEnabled(loaded.globalSearchEnabled);
        setGlobalSearchLinksEnabled(loaded.globalSearchLinksEnabled);

        setGroundingProductionText(loaded.groundingProductionText);
        groundingLastSubmittedRef.current = loaded.groundingProductionText;
        productionBaselinesRef.current.grounding = loaded.groundingProductionText;
        groundingTextRef.current = loaded.groundingText;
        setGroundingText(loaded.groundingText);
        setGroundingIsDirty(
          draft?.groundingText !== undefined &&
            !draftTextEqual(draft.groundingText, loaded.groundingProductionText),
        );

        reconcileDraftBanner();
      })
      .catch(() => {
        /* non-fatal */
      });

    refreshPreviewGoogleSearch(chatIdRef.current)
      .then((data) => {
        setSandboxSearchEnabled(data.sandboxEnabled);
        setSandboxSearchLocked(data.sandboxToggleLocked);
      })
      .catch(() => {
        /* non-fatal */
      });

    if (draftSandboxSearch === true) {
      setSandboxSearchEnabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (productTab !== 0) return;
    refreshPreviewGoogleSearch(chatIdRef.current)
      .then((data) => {
        setSandboxSearchEnabled(data.sandboxEnabled);
        setSandboxSearchLocked(data.sandboxToggleLocked);
      })
      .catch(() => {
        /* non-fatal */
      });
  }, [productTab]);

  const handleRawMarkdownChange = (value: string) => {
    userHasEditedRef.current = true;
    setMarkdown(value);
    if (lastSubmittedMarkdownRef.current !== null) {
      setIsDirty(value.trim() !== lastSubmittedMarkdownRef.current.trim());
    }
    scheduleDraftSave(
      value,
      workflow.selectedContact,
      intentTextRef.current,
      groundingTextRef.current,
    );
    setPromptSynced(false);
    if (savePromptTimerRef.current) clearTimeout(savePromptTimerRef.current);
    savePromptTimerRef.current = setTimeout(() => syncPromptNow(value), 1200);
  };

  const handleIntentChange = (value: string) => {
    userHasEditedRef.current = true;
    intentTextRef.current = value;
    setIntentText(value);
    if (intentLastSubmittedRef.current !== null) {
      setIntentIsDirty(value.trim() !== intentLastSubmittedRef.current.trim());
    }
    scheduleDraftSave(markdown, workflow.selectedContact, value, groundingTextRef.current);
    setIntentSynced(false);
    if (saveIntentTimerRef.current) clearTimeout(saveIntentTimerRef.current);
    saveIntentTimerRef.current = setTimeout(() => {
      if (value.trim()) {
        syncIntentPrompt(value)
          .then(() => setIntentSynced(true))
          .catch(() => setIntentSynced(false));
      }
    }, 1200);
  };

  const resetIntentToProduction = () => {
    intentTextRef.current = intentProductionText;
    setIntentText(intentProductionText);
    setIntentIsDirty(false);
    if (intentProductionText.trim()) {
      syncIntentPrompt(intentProductionText).catch(() => {
        /* non-fatal */
      });
    }
  };

  const handleGroundingChange = (value: string) => {
    userHasEditedRef.current = true;
    groundingTextRef.current = value;
    setGroundingText(value);
    scheduleDraftSave(markdown, workflow.selectedContact, intentTextRef.current, value);
    if (groundingLastSubmittedRef.current !== null) {
      setGroundingIsDirty(value.trim() !== groundingLastSubmittedRef.current.trim());
    }
    setGroundingSynced(false);
    if (saveGroundingTimerRef.current) clearTimeout(saveGroundingTimerRef.current);
    saveGroundingTimerRef.current = setTimeout(() => syncGroundingNow(value), 1200);
  };

  const resetGroundingToProduction = () => {
    groundingTextRef.current = groundingProductionText;
    setGroundingText(groundingProductionText);
    setGroundingIsDirty(false);
    syncGroundingNow(groundingProductionText);
  };

  const syncEditorFromMarkdown = () => {
    editor?.commands.setContent(markdownToTiptapJson(markdown) as any);
  };

  return {
    proposalId,
    productTab,
    setProductTab,
    investorPromptTab,
    setInvestorPromptTab,
    rawMode,
    setRawMode,
    markdown,
    promptSynced,
    isDirty,
    intentText,
    intentProductionText,
    intentSynced,
    intentIsDirty,
    groundingText,
    groundingProductionText,
    groundingSynced,
    groundingIsDirty,
    globalSearchEnabled,
    sandboxSearchEnabled,
    sandboxSearchLocked,
    showGroundingPromptTab,
    globalGuideOptions,
    savedDraft,
    editor,
    chatId: chatIdRef.current,
    ...workflow,
    handleRawMarkdownChange,
    handleIntentChange,
    resetIntentToProduction,
    handleGroundingChange,
    resetGroundingToProduction,
    syncEditorFromMarkdown,
  };
}

export type VeraGlobalChatPromptOverrideState = ReturnType<typeof useVeraGlobalChatPromptOverride>;
